/**
 * AERO2 — About Us
 *
 * Pure scroll/animation maths plus the DOM layer for templates/page.about.liquid.
 * The logic is exported so it can be unit-tested under Node without a DOM; this
 * mirrors how assets/home.js, assets/help.js and assets/cart-utils.js are built.
 *
 * Owns six behaviours: the measured header offset, scroll reveals, word-by-word
 * headline reveals, the chapter-driven sticky stage, the count-up figures, and
 * scroll parallax. Native APIs only, no dependencies.
 */
(function () {
  'use strict';

  /* ===== Pure logic (no DOM access below this line) ===== */

  /**
   * Constrain a value to a range.
   *
   * @param {number} value
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  function clamp(value, min, max) {
    if (typeof value !== 'number' || !isFinite(value)) return min;
    if (value < min) return min;
    if (value > max) return max;
    return value;
  }

  /**
   * Exponential ease-out over a normalised 0..1 progress.
   *
   * Chosen over a linear ramp because a counter that decelerates reads as a
   * measurement settling rather than a clock ticking.
   *
   * @param {number} t - progress, clamped to [0, 1]
   * @returns {number} eased progress in [0, 1]
   */
  function easeOutExpo(t) {
    const progress = clamp(t, 0, 1);
    if (progress >= 1) return 1;
    return 1 - Math.pow(2, -10 * progress);
  }

  /**
   * The integer a count-up should display part way through its run.
   *
   * @param {number} target - the final figure
   * @param {number} elapsed - ms since the count started
   * @param {number} duration - ms the count should take
   * @returns {number} an integer in [0, target]
   */
  function countAt(target, elapsed, duration) {
    if (typeof target !== 'number' || !isFinite(target)) return 0;
    if (typeof duration !== 'number' || duration <= 0) return target;
    return Math.round(target * easeOutExpo(elapsed / duration));
  }

  /**
   * Render a counter value with its optional affixes.
   *
   * Both are markup-supplied ("+" or "×") and carry meaning — "100", "100+" and
   * "+1" are different claims — so neither is dropped mid-count. Which side the
   * "+" goes on is a copy decision, which is why the markup owns it: the page
   * reads "+1 year of testing" but "100+ prototypes".
   *
   * @param {number} value
   * @param {string} [suffix]
   * @param {string} [prefix]
   * @returns {string}
   */
  function formatCount(value, suffix, prefix) {
    return (
      (typeof prefix === 'string' ? prefix : '') +
      String(value) +
      (typeof suffix === 'string' ? suffix : '')
    );
  }

  /**
   * How far a parallax element should be displaced at its current scroll position.
   *
   * Progress runs 0 → 1 across the whole time the element overlaps the viewport,
   * and the result is centred on that range so the element sits unshifted when it
   * is halfway through. A positive strength drifts it with the scroll (slower than
   * the page); a negative one drives it against (faster).
   *
   * @param {number} top - the element's viewport-relative top, in px
   * @param {number} height - its rendered height, in px
   * @param {number} viewportHeight - in px
   * @param {number} strength - peak displacement in px, may be negative
   * @returns {number} px to translate, rounded to 2dp
   */
  function parallaxShift(top, height, viewportHeight, strength) {
    if (typeof top !== 'number' || !isFinite(top)) return 0;
    if (typeof height !== 'number' || height <= 0) return 0;
    if (typeof viewportHeight !== 'number' || viewportHeight <= 0) return 0;
    if (typeof strength !== 'number' || !isFinite(strength)) return 0;

    const travelled = (viewportHeight - top) / (viewportHeight + height);
    return Math.round((clamp(travelled, 0, 1) - 0.5) * 2 * strength * 100) / 100;
  }

  /**
   * Fold a batch of IntersectionObserver updates into the chapter the sticky
   * stage should be showing.
   *
   * The observer watches a band across the middle of the window (see initStory
   * below), so at most one chapter is intersecting at a time. Anything not
   * intersecting is ignored rather than treated as a reason to change: at the two
   * ends of the section nothing crosses the band at all, and the stage has to
   * hold its current frame instead of snapping back to the first one.
   *
   * @param {number} current - the chapter on screen now
   * @param {Array<{index: number, isIntersecting: boolean}>} updates
   * @param {number} total - number of chapters
   * @returns {number} the chapter index to show
   */
  function resolveChapter(current, updates, total) {
    if (!Array.isArray(updates) || !total || total < 1) return current;

    let next = current;

    for (let i = 0; i < updates.length; i += 1) {
      const update = updates[i];
      if (!update || !update.isIntersecting) continue;

      const index = update.index;
      /* A chapter the markup never declared must not be able to move the stage. */
      if (typeof index !== 'number' || index < 0 || index >= total) continue;
      if (Math.floor(index) !== index) continue;

      next = index;
    }

    return next;
  }

  /**
   * The "01 / 04" label for the sticky stage.
   *
   * @param {number} index - zero-based
   * @param {number} total
   * @returns {string}
   */
  function stageLabel(index, total) {
    const pad = (n) => (n < 10 ? '0' + n : String(n));
    return pad(index + 1) + ' / ' + pad(total);
  }

  /**
   * How much room the site header takes off the top of the window.
   *
   * sections/header.liquid pins .aero-navbar-section at top: 0 when its sticky
   * setting is on, and that setting defaults to on — so anything else pinned at
   * top: 0 disappears underneath it. A header that is not pinned scrolls away and
   * reserves nothing, which is why the position matters as much as the height.
   *
   * @param {string} position - the header's computed `position`
   * @param {number} height - its rendered height in px
   * @returns {number} px to clear, 0 when the header does not stay on screen
   */
  function headerOffset(position, height) {
    if (position !== 'sticky' && position !== 'fixed') return 0;
    if (typeof height !== 'number' || !isFinite(height) || height <= 0) return 0;
    return Math.round(height);
  }

  /**
   * Escape a character for insertion as HTML.
   *
   * @param {string} text
   * @returns {string}
   */
  function escapeHtml(text) {
    if (text === null || text === undefined) return '';
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * Split a run of copy into cells for the left-to-right character reveal.
   *
   * Whitespace comes back as a gap (`index: null`) rather than a cell. Two
   * reasons: an invisible character has nothing to reveal, and leaving the
   * spaces as plain text between the spans is what lets the browser wrap the
   * paragraph at its own line breaks instead of at every glyph.
   *
   * A gap still consumes its position in the count, so the sweep advances evenly
   * across the line rather than speeding up over every space.
   *
   * Array.from, not split(''), so an astral character stays one cell instead of
   * being torn into two broken halves — the same reason text-roll.js does it.
   *
   * @param {string} text
   * @param {number} [start] - first index, for a run continuing a previous one
   * @returns {Array<{char: string, index: number|null}>}
   */
  function splitReveal(text, start) {
    if (text === null || text === undefined) return [];

    const base = typeof start === 'number' && isFinite(start) ? start : 0;
    return Array.from(String(text)).map((char, offset) => ({
      char,
      index: /\s/.test(char) ? null : base + offset,
    }));
  }

  /**
   * The markup for one split run.
   *
   * The cells are wrapped, and the wrapper is not optional. Two of the three
   * elements this splits — .about-story__list-item and .about-specs__item — are
   * flex containers, so without it every character becomes a flex item spread by
   * the container's `gap`, and the spaces between them vanish outright: a
   * whitespace-only anonymous flex item is not rendered at all. The wrapper is
   * the single flex item those rules were written for, and inline layout inside
   * it puts the spaces and the line wrapping back.
   *
   * @param {string} text
   * @param {number} [start]
   * @returns {string}
   */
  function revealMarkup(text, start) {
    const cells = splitReveal(text, start)
      .map((cell) =>
        cell.index === null
          ? escapeHtml(cell.char)
          : '<span class="about-char" style="--c:' + cell.index + '">' + escapeHtml(cell.char) + '</span>'
      )
      .join('');

    return cells ? '<span class="about-chars">' + cells + '</span>' : '';
  }

  /**
   * How long a split run needs before nothing is left animating.
   *
   * Used to schedule the class that drops the blur filters — see the note on
   * `.is-settled` in about-us.css.
   *
   * @param {number} count - characters in the run
   * @param {number} stagger - ms between characters
   * @param {number} duration - ms of one character's transition
   * @returns {number} ms
   */
  function revealDuration(count, stagger, duration) {
    const cells = typeof count === 'number' && count > 0 ? count : 0;
    return cells * stagger + duration;
  }

  /**
   * The `.about-word` / `.about-word__in` markup for one word-reveal run —
   * the same shape templates/page.about.liquid already builds with a Liquid
   * `| split: ' '` for-loop for the hero headline. The peloton quote used to
   * be that same for-loop over a hardcoded literal; now the literal lives in
   * `locales/en.default.json` and reaches this file through the
   * `data-string-quote` attribute (see readStrings in the DOM layer below),
   * so this is the JS-side equivalent of that Liquid loop rather than a
   * second copy of the English sentence.
   *
   * @param {string} text
   * @returns {string}
   */
  function wordRevealMarkup(text) {
    if (text === null || text === undefined) return '';

    const words = String(text)
      .split(' ')
      .filter((word) => word !== '');

    return words
      .map(
        (word, i) =>
          '<span class="about-word" style="--w: ' +
          i +
          ';"><span class="about-word__in">' +
          escapeHtml(word) +
          '</span></span>'
      )
      .join('');
  }

  const api = {
    clamp,
    easeOutExpo,
    countAt,
    formatCount,
    parallaxShift,
    resolveChapter,
    stageLabel,
    headerOffset,
    escapeHtml,
    splitReveal,
    revealMarkup,
    revealDuration,
    wordRevealMarkup,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.AeroAbout = api;

  /* ===== DOM layer — everything below needs a browser ===== */

  if (typeof document === 'undefined') return;

  const root = document.querySelector('.about');
  if (!root) return;

  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const hasObserver = 'IntersectionObserver' in window;

  /* ── 0. Strings ──
     The English fallback below is a second copy of the sentence, but only as
     a safety net: templates/page.about.liquid writes the real value onto
     data-string-quote from the same locale key this reads, the way
     assets/cart.js's readStrings reads what Liquid wrote onto
     .aero-cart-page so the two never disagree. */
  function readStrings() {
    const data = root.dataset || {};
    return {
      quote: data.stringQuote || 'I had never tried a strip like this.',
    };
  }

  const strings = readStrings();

  /* The blockquote already carries this sentence, split word by word by a
     Liquid `| split: ' '` for-loop, so the page is correct before this file
     runs. Rebuilding it here from the same string keeps the markup in one
     place (wordRevealMarkup) instead of duplicated between Liquid and JS. */
  const quoteEl = root.querySelector('.about-peloton__quote');
  if (quoteEl) {
    const quoteMarkup = wordRevealMarkup(strings.quote);
    if (quoteMarkup) quoteEl.innerHTML = quoteMarkup;
  }

  /* ── 1. Header offset ──
     The sticky rail and stage clear the header by var(--about-header-h); the
     stylesheet ships a sensible guess and this replaces it with the real one. */
  function syncHeaderOffset() {
    const header = document.querySelector('.aero-navbar-section');
    if (!header) return;

    const offset = headerOffset(
      window.getComputedStyle(header).position,
      header.getBoundingClientRect().height
    );
    if (offset > 0) root.style.setProperty('--about-header-h', offset + 'px');
  }

  syncHeaderOffset();
  window.addEventListener('resize', syncHeaderOffset, { passive: true });

  /* ── 2. Character split (story section only) ──
     Runs before the reveal observer, so a block is already marked .is-split by
     the time .is-visible lands on it and never blurs as a block and as
     characters at once.

     Skipped entirely under reduced motion: there is no sweep to stage, and
     tearing the copy into several hundred spans for nothing is pure cost. That
     also leaves the copy as ordinary text nodes, which is the safest thing to
     hand a screen reader. */
  const CHAR_STAGGER = 8;
  const CHAR_DURATION = 450;
  const BLOCK_STAGGER = 110;
  const splitCounts = new WeakMap();

  /**
   * Tear every [data-split] run inside one reveal block into character spans.
   * The index runs across the whole block, so a paragraph and the list under it
   * sweep as one continuous run rather than restarting halfway.
   */
  function splitBlock(block) {
    const targets = block.matches('[data-split]')
      ? [block]
      : Array.prototype.slice.call(block.querySelectorAll('[data-split]'));

    let index = 0;

    targets.forEach((target) => {
      /* innerHTML below would tear any real markup apart. Nothing marked for
         splitting carries any today; this is what keeps that true. */
      if (target.children.length) return;

      const text = target.textContent.replace(/\s+/g, ' ').trim();
      if (!text) return;

      target.innerHTML = revealMarkup(text, index);
      index += Array.from(text).length;
    });

    if (!index) return;
    block.classList.add('is-split');
    splitCounts.set(block, index);
  }

  if (!prefersReducedMotion) {
    root.querySelectorAll('.about-story .about-reveal').forEach(splitBlock);
  }

  /* ── 3. Scroll reveals and word-by-word headlines ──
     Both are one-shot: add .is-visible once, then stop watching. */
  const revealEls = root.querySelectorAll('.about-reveal, [data-words], .about-define__panel');

  function showReveal(el) {
    el.classList.add('is-visible');
    /* The panel drives its own strike/underline timeline off a second class so
       the swap can settle after the panel itself has finished arriving. */
    if (el.classList.contains('about-define__panel')) el.classList.add('is-settled');

    /* A split run leaves a blur filter on every character, and a filter makes
       each one its own stacking context. Once the sweep has finished there is
       nothing left to animate, so drop them. */
    const count = splitCounts.get(el);
    if (!count) return;

    const blockDelay = (parseFloat(el.style.getPropertyValue('--i')) || 0) * BLOCK_STAGGER;
    window.setTimeout(
      () => el.classList.add('is-settled'),
      blockDelay + revealDuration(count, CHAR_STAGGER, CHAR_DURATION)
    );
  }

  if (hasObserver && !prefersReducedMotion) {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          showReveal(entry.target);
          revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    revealEls.forEach((el) => revealObserver.observe(el));
  } else {
    revealEls.forEach(showReveal);
  }

  /* ── 3. Count-up figures ──
     The markup already holds the final value, so this rewinds to zero first and
     only when it is actually going to animate. */
  const counters = root.querySelectorAll('[data-count-to]');
  const COUNT_DURATION = 1600;

  function runCounter(el) {
    const target = parseFloat(el.getAttribute('data-count-to'));
    if (!isFinite(target)) return;

    const suffix = el.getAttribute('data-count-suffix') || '';
    const prefix = el.getAttribute('data-count-prefix') || '';
    const start = performance.now();

    function step(now) {
      const value = countAt(target, now - start, COUNT_DURATION);
      el.textContent = formatCount(value, suffix, prefix);
      if (now - start < COUNT_DURATION) requestAnimationFrame(step);
      else el.textContent = formatCount(target, suffix, prefix);
    }

    el.textContent = formatCount(0, suffix, prefix);
    requestAnimationFrame(step);
  }

  if (hasObserver && !prefersReducedMotion) {
    const counterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          counterObserver.unobserve(entry.target);
          runCounter(entry.target);
        });
      },
      { threshold: 0.6 }
    );

    counters.forEach((el) => counterObserver.observe(el));
  }

  /* ── 4. Parallax ──
     One rAF-coalesced pass writes a custom property per element; the stylesheet
     decides what to do with it, so a rule can opt out per breakpoint. */
  const parallaxEls = root.querySelectorAll('[data-parallax]');
  let parallaxQueued = false;

  function paintParallax() {
    parallaxQueued = false;
    const viewportHeight = window.innerHeight;

    parallaxEls.forEach((el) => {
      const strength = parseFloat(el.getAttribute('data-parallax'));
      const rect = el.getBoundingClientRect();
      const shift = parallaxShift(rect.top, rect.height, viewportHeight, strength);
      el.style.setProperty('--about-parallax', shift + 'px');
    });
  }

  function queueParallax() {
    if (parallaxQueued) return;
    parallaxQueued = true;
    requestAnimationFrame(paintParallax);
  }

  if (parallaxEls.length && !prefersReducedMotion) {
    queueParallax();
    window.addEventListener('scroll', queueParallax, { passive: true });
    window.addEventListener('resize', queueParallax, { passive: true });
  }

  /* ── 5. Chapter → sticky stage ── */
  const chapters = root.querySelectorAll('.about-story__chapter');
  const layers = root.querySelectorAll('.about-story__layer');
  const railDots = root.querySelectorAll('.about-story__rail-dot');
  const stageIndex = root.querySelector('.about-story__stage-index');

  if (!chapters.length || !layers.length) return;

  const chapterList = Array.prototype.slice.call(chapters);
  let currentChapter = 0;

  /**
   * Crossfade the stage to a chapter, mark the rail and update the counter.
   *
   * @param {number} index - zero-based chapter index
   */
  function setChapter(index) {
    currentChapter = index;

    layers.forEach((layer, i) => layer.classList.toggle('is-active', i === index));
    railDots.forEach((dot, i) => dot.classList.toggle('is-active', i <= index));
    chapters.forEach((chapter, i) => chapter.classList.toggle('is-current', i === index));

    if (stageIndex) stageIndex.textContent = stageLabel(index, chapters.length);
  }

  setChapter(0);

  if (!hasObserver) return;

  const chapterObserver = new IntersectionObserver(
    (entries) => {
      const updates = entries.map((entry) => ({
        index: chapterList.indexOf(entry.target),
        isIntersecting: entry.isIntersecting,
      }));

      const next = resolveChapter(currentChapter, updates, chapters.length);
      if (next !== currentChapter) setChapter(next);
    },
    /* A thin band across the middle of the window, not a threshold: chapters are
       taller than the viewport, so a threshold would be reached twice per chapter
       and two neighbours could hold it at once. The band is 1% rather than 0
       because a zero-height root has been handled inconsistently by engines. */
    { rootMargin: '-49.5% 0px -49.5% 0px', threshold: 0 }
  );

  chapters.forEach((chapter) => chapterObserver.observe(chapter));
})();
